import {SubAPI} from "./SubAPI";
import {TpCatalogHeader, TpGroup, TpPrepTarget, TpRule} from "./TpLibrary";

export type TpTmplDocKind = "index" | "template" | "instruction" | "unknown";

export type TpTmplSource = {
    id: number;
    manufacturer: number;
    kind: string;
    url: string;
    brand: string;
    lastSyncedAt: string | null;
}

export type TpTmplSourceListItem = TpTmplSource & {
    manufacturerName: string;
    docCount: number;
    fetchedCount: number;
    failedCount: number;
    linkCount: number;
}

export type TpTmplEntry = {
    id: number;
    tpTmplDoc: number;
    pageNo: number;
    label: string;
    templateNo: string;
    targetDoc: number | null;
    srcText: string;
}

export type TpTmplDoc = {
    id: number;
    tpTmplSource: number;
    manufacturer: number;
    parent: number | null;
    parentTitle: string | null;
    depth: number;
    section: string;
    title: string;
    url: string;
    docNo: string;
    templateNo: string;
    docKind: TpTmplDocKind;
    file: number | null;
    pageCount: number;
    fetchedAt: string | null;
    fetchError: string;
    linkCount: number;
    entries: TpTmplEntry[];
}

export type TpTmplDocListItem = Omit<TpTmplDoc, "entries"> & {
    manufacturerName: string;
}

export type TpTmplEntryDetail = TpTmplEntry & {
    targetTitle: string | null;
}

// TpTmplDocRef names the template a group or rule points at and the index row that supports it.
export type TpTmplDocRef = {
    docTitle: string | null;
    docTemplateNo: string | null;
    entryDoc: number | null;
    entryPageNo: number | null;
    entryLabel: string | null;
    entrySrcText: string | null;
}

export type TpTmplPrepRule = Omit<TpRule, "sources"> & TpTmplDocRef;

export type TpTmplGroup = TpGroup & TpTmplDocRef & {
    prepRules: TpTmplPrepRule[];
}

// TpTmplDocLink is one group that uses a doc, directly (tpRawRule null) or through a prep rule.
export type TpTmplDocLink = {
    tpRawGroup: number;
    groupCode: string;
    groupName: string;
    tpCatalog: number;
    catalogName: string;
    tpRawRule: number | null;
    ruleName: string;
}

export type TpTmplTemplateDetail = TpTmplDocListItem & {
    source: TpTmplSource;
    children: TpTmplDocListItem[];
    entries: TpTmplEntryDetail[];
    links: TpTmplDocLink[];
}

export type TpTmplCatalogDetail = {
    catalog: TpCatalogHeader;
    source: TpTmplSource | null;
    groups: TpTmplGroup[];
    docs: TpTmplDoc[];
}

export class TpLibraryTmpl extends SubAPI {
    // id is the tp_catalog id
    getCatalog(props: { id: number }) {
        return this.fetcher.get<TpTmplCatalogDetail>("/api/tp-library/tmpl/catalog", props);
    }

    listDocs(props: {}) {
        return this.fetcher.get<TpTmplDocListItem[]>("/api/tp-library/tmpl/docs", props);
    }

    // id is the tp_tmpl_doc id
    getDoc(props: { id: number }) {
        return this.fetcher.get<TpTmplTemplateDetail>("/api/tp-library/tmpl/doc", props);
    }

    listSources(props: {}) {
        return this.fetcher.get<TpTmplSourceListItem[]>("/api/tp-library/tmpl/sources", props);
    }

    // id is the tp_raw_group id; tpTmplDoc null clears the group template
    setGroupDoc(props: { id: number; tpTmplDoc: number | null }) {
        return this.fetcher.post<{}>("/api/tp-library/tmpl/group-doc", props);
    }

    // id is the tp_raw_rule id of a prep rule; tpTmplDoc null clears its template
    setRuleDoc(props: { id: number; tpTmplDoc: number | null }) {
        return this.fetcher.post<{}>("/api/tp-library/tmpl/rule-doc", props);
    }

    // id = 0 creates a template-specific prep rule for the group; id set with archived = true removes it
    upsertPrepRule(props: {
        id: number;
        tpRawGroup: number;
        tpTmplDoc: number;
        prepTarget: TpPrepTarget;
        statement: string;
        archived: boolean;
        options: { tpRawGroupOption: number; tpRawGroupOptionValue: number | null }[];
    }) {
        return this.fetcher.post<{ id: number }>("/api/tp-library/tmpl/prep-rule", props);
    }

    pdfUrl(props: { id: number }) {
        return "/api/tp-library/tmpl/doc/pdf?id=" + props.id;
    }
}
